import type { LlmPriceEntry } from "@/app/_api/elevenlabs/llmPrice";

// First-choice models when nothing is selected yet; falls back to the cheapest priced model.
export const DEFAULT_MODEL_PRIORITY = ["gemini-2.5-flash", "gpt-4o-mini", "gpt-4o", "claude-haiku-4-5"];

// Curated allowlist of the models surfaced in the dropdown, grouped by provider and mapped to their
// display labels. The endpoint returns ~90 ids (incl. $0 placeholders, internal/open models and
// dated snapshots like `gpt-4o-2024-08-06`); we only feature these canonical, current models so the
// picker mirrors the in-app LLM selector. Provider order here is the section order in the dropdown;
// membership here == "featured". Within a section the dropdown sorts models cheapest-first.
interface ModelProvider {
  id: string;
  label: string;
  /** model id → display label */
  models: Record<string, string>;
}

const MODEL_PROVIDERS: ModelProvider[] = [
  {
    id: "elevenlabs",
    label: "ElevenLabs",
    models: {
      "qwen35-397b-a17b": "Qwen3.5-397B-A17B",
      "qwen36-35b-a3b": "Qwen3.6-35B-A3B",
    },
  },
  {
    id: "anthropic",
    label: "Anthropic",
    models: {
      "claude-opus-4-7": "Claude Opus 4.7",
      "claude-sonnet-4-6": "Claude Sonnet 4.6",
      "claude-sonnet-4-5": "Claude Sonnet 4.5",
      "claude-sonnet-4": "Claude Sonnet 4",
      "claude-haiku-4-5": "Claude Haiku 4.5",
    },
  },
  {
    id: "google",
    label: "Google",
    models: {
      "gemini-3.1-pro-preview": "Gemini 3.1 Pro Preview",
      "gemini-3.5-flash": "Gemini 3.5 Flash",
      "gemini-3-flash-preview": "Gemini 3 Flash Preview",
      "gemini-3.1-flash-lite": "Gemini 3.1 Flash Lite",
      "gemini-3.1-flash-lite-preview": "Gemini 3.1 Flash Lite Preview",
      "gemini-2.5-flash": "Gemini 2.5 Flash",
      "gemini-2.5-flash-lite": "Gemini 2.5 Flash Lite",
    },
  },
  {
    id: "openai",
    label: "OpenAI",
    models: {
      "gpt-5": "GPT-5",
      "gpt-5.1": "GPT-5.1",
      "gpt-5.2": "GPT-5.2",
      "gpt-5.4": "GPT-5.4",
      "gpt-5.5": "GPT-5.5",
      "gpt-5.4-mini": "GPT-5.4 Mini",
      "gpt-5.4-nano": "GPT-5.4 Nano",
      "gpt-5-mini": "GPT-5 Mini",
      "gpt-5-nano": "GPT-5 Nano",
      "gpt-4.1": "GPT-4.1",
      "gpt-4.1-mini": "GPT-4.1 Mini",
      "gpt-4.1-nano": "GPT-4.1 Nano",
      "gpt-4o": "GPT-4o",
      "gpt-4o-mini": "GPT-4o Mini",
    },
  },
];

// Flattened id → label across every provider, for O(1) label lookup and featured-membership checks.
const MODEL_LABELS: Record<string, string> = Object.fromEntries(
  MODEL_PROVIDERS.flatMap(provider => Object.entries(provider.models)),
);

/** Display label for a model id, falling back to the raw id for un-curated models. */
export const modelLabel = (id: string) => MODEL_LABELS[id] ?? id;

/** Only featured models (those with a curated label) are offered in the picker. */
export const isFeaturedModel = (id: string) => id in MODEL_LABELS;

/** Featured models only, sorted cheapest-first by per-minute price. */
export const featuredByPrice = (prices: LlmPriceEntry[]) =>
  prices.filter(p => isFeaturedModel(p.llm)).sort((a, b) => a.pricePerMinute - b.pricePerMinute);

export interface ModelProviderGroup {
  id: string;
  label: string;
  models: LlmPriceEntry[];
}

/**
 * Featured models grouped by provider (provider order preserved), cheapest-first within each group.
 * Providers with no priced model in this scenario are dropped, so the dropdown only shows real options.
 */
export const groupedByProvider = (prices: LlmPriceEntry[]): ModelProviderGroup[] =>
  MODEL_PROVIDERS.map(provider => ({
    id: provider.id,
    label: provider.label,
    models: Object.keys(provider.models)
      .map(id => prices.find(p => p.llm === id))
      .filter((p): p is LlmPriceEntry => p !== undefined)
      .sort((a, b) => a.pricePerMinute - b.pricePerMinute),
  })).filter(group => group.models.length > 0);

/**
 * Picks the model to preselect: the first available from {@link DEFAULT_MODEL_PRIORITY}, otherwise
 * the cheapest *featured* model (raw endpoint ids aren't offered), or "" when nothing is priced.
 */
export const pickDefaultModel = (prices: LlmPriceEntry[]) => {
  for (const id of DEFAULT_MODEL_PRIORITY) {
    if (prices.some(p => p.llm === id)) return id;
  }
  return featuredByPrice(prices)[0]?.llm ?? "";
};
